import axios from 'axios';
import config from '@/config';
import * as constants from '../constants';

const { endpoints } = config.checkout;

const fetchCart = ({ commit, dispatch }) => {
  commit(constants.CHECKOUT_CART_LOADING, true);
  return new Promise((resolve, reject) => {
    axios({ url: endpoints.cart })
      .then(({ data }) => {
        commit(constants.CHECKOUT_CART, data);
        resolve(data);
      })
      .catch(err => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      })
      .finally(() => {
        commit(constants.CHECKOUT_CART_LOADING, false);
      });
  });
};

const fetchSimpleCart = ({ commit, dispatch }) => {
  commit(constants.CHECKOUT_CART_LOADING, true);
  return new Promise((resolve, reject) => {
    axios({ url: endpoints.simpleCart })
      .then(({ data }) => {
        commit(constants.CHECKOUT_CART, data);
        resolve(data);
      })
      .catch(err => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      })
      .finally(() => {
        commit(constants.CHECKOUT_CART_LOADING, false);
      });
  });
};

const fetchTotals = ({ commit, dispatch, getters }, data = {}) => {
  commit(constants.CHECKOUT_TOTALS_LOADING, true);
  return new Promise((resolve, reject) => {
    axios({
      url: endpoints.totals,
      data: {
        ...getters.defaultTotalsConfig,
        ...data,
      },
      method: 'POST',
    })
      .then(({ data: totals }) => {
        commit(constants.CHECKOUT_TOTALS, totals);
        resolve(totals);
      })
      .catch(err => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      })
      .finally(() => {
        commit(constants.CHECKOUT_TOTALS_LOADING, false);
      });
  });
};

const clearCart = ({ dispatch }, data = {}) => new Promise((resolve, reject) => {
  axios({ url: endpoints.clearCart, data, method: 'POST' })
    .then(resp => {
      dispatch('fetchCart');
      dispatch('fetchTotals');
      resolve(resp);
    })
    .catch(err => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const confirmCheckout = ({ dispatch, getters }, data = {}) => new Promise((resolve, reject) => {
  axios({
    url: endpoints.confirmCheckout,
    data: {
      ...getters.defaultTotalsConfig,
      ...data,
    },
    method: 'POST',
  })
    .then(({ data: confirm }) => {
      if (confirm.Success) {
        dispatch(
          'core/addNotification',
          {
            title: 'Success',
            message: 'Your order was successfully placed',
            type: 'success',
          },
          { root: true },
        );
        dispatch('fetchCart');
        resolve(confirm);
      } else {
        const errors = confirm.Errors ? confirm.Errors.join(', ') : 'Something went wrong!';
        throw new Error(errors);
      }
    })
    .catch(err => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const addItemToCart = ({ dispatch, rootGetters }, cart = {}) => {
  const { marke, produktart } = rootGetters['level4/vehicleData']?.VehicleInfo || {};
  const data = {
    marke: marke || 'BMW',
    produktart: produktart || 'PKW',
    ...cart,
  };

  return new Promise((resolve, reject) => {
    axios({ url: endpoints.addItem, data, method: 'POST' })
      .then(resp => {
        const isInfo = resp.data.length;
        dispatch(
          'core/addNotification',
          {
            title: isInfo ? 'Info' : 'Success',
            message: isInfo ? resp.data[0] : 'Product added to cart',
            type: isInfo ? 'info' : 'success',
          },
          { root: true },
        );
        dispatch('fetchTotals');
        resolve(resp);
      })
      .catch(err => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      });
  });
};

const addItemsFromCsvToCart = ({ dispatch }, data = []) => new Promise((resolve, reject) => {
  axios({ url: endpoints.addItemsFromCsvCart, data, method: 'POST' })
    .then(resp => {
      const isInfo = resp.data.length;
      dispatch(
        'core/addNotification',
        {
          title: isInfo ? 'Info' : 'Success',
          message: isInfo ? resp.data[0] : 'Products added to cart',
          type: isInfo ? 'info' : 'success',
        },
        { root: true },
      );
      dispatch('fetchTotals');
      resolve(resp);
    })
    .catch(err => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const deleteCartItem = ({ dispatch }, data = {}) => new Promise((resolve, reject) => {
  axios({ url: endpoints.deleteItem, data: { Quantity: 0, ...data }, method: 'POST' })
    .then(resp => {
      dispatch(
        'core/addNotification',
        {
          title: 'Success',
          message: 'Product removed from cart',
          type: 'success',
        },
        { root: true },
      );
      dispatch('fetchCart');
      dispatch('fetchTotals');
      resolve(resp);
    })
    .catch(err => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const updateCartItem = ({ dispatch }, data = {}) => new Promise((resolve, reject) => {
  axios({ url: endpoints.updateItem, data, method: 'POST' })
    .then(resp => {
      dispatch('fetchSimpleCart');
      resolve(resp);
    })
    .catch(err => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const fetchShippingMethods = ({ commit, dispatch }, id) => new Promise((resolve, reject) => {
  axios({ url: endpoints.shippingMethods(id) })
    .then(({ data }) => {
      commit(constants.CHECKOUT_SHIPPING_METHODS, data);
      resolve(data);
    })
    .catch(err => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const fetchPaymentMethods = ({ commit, dispatch }, countryId) => new Promise((resolve, reject) => {
  axios({ url: endpoints.paymentMethods(countryId) })
    .then(({ data }) => {
      commit(constants.CHECKOUT_PAYMENT_METHODS, data);
      resolve(data);
    })
    .catch(err => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const fetchShippingAddress = ({ commit, dispatch }) => new Promise((resolve, reject) => {
  axios({ url: endpoints.shippingAddress, method: 'GET' })
    .then(({ data }) => {
      commit(constants.CHECKOUT_SHIPPING_ADDRESS, data);
      resolve(data);
    })
    .catch(err => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

export default {
  confirmCheckout,
  fetchCart,
  fetchSimpleCart,
  fetchTotals,
  clearCart,
  fetchPaymentMethods,
  fetchShippingAddress,
  fetchShippingMethods,
  addItemToCart,
  addItemsFromCsvToCart,
  deleteCartItem,
  updateCartItem,
};
